import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ReactSVG } from "react-svg";
import { Form, Button, Header, Grid, Loader, Dimmer, Icon } from 'semantic-ui-react';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import type { SweetAlertOptions } from 'sweetalert2';
import axiosInstance from '../../helpers/axiosInstance';
import { utils } from '../../helpers/utils';
import { AddressBookModel } from '../../types/AddressBookDTO';
import BrightnessContrast from "../../assets/images/brightness-contrast.svg";
import UserInfo from '../Common/SharedComponents/UserInfo';
import AppContext, { token_config } from '../../AppContext';

type FormErrorFields = {
  email: string;
};

export const AddressBookForm: React.FC = () => {
  const navigate = useNavigate();
  const { userInfo } = useContext(AppContext);

  useEffect(() => {
    if (userInfo !== undefined) {
      const privileges: string[] = userInfo ? userInfo.Privileges.split(',') : [];
      if (!privileges.includes(token_config.MANAGE_ADDRESS_BOOKS)) {
        navigate('/');
      }
    }
  }, [userInfo, navigate]);

  const { id } = useParams();

  let addressBookId = id ? parseInt(id) : 0;

  const { handleThemeChange, isNavOpen, handleShowNav } = useContext(AppContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<AddressBookModel>({} as AddressBookModel);
  const [formErrors, setFormErrors] = useState<FormErrorFields>({
    email: ''
  });

  useEffect(() => {
    if (addressBookId > 0) {
      document.title = 'Edit Email - Adjure';
      fetchData();
    } else {
      document.title = 'Add Email - Adjure';
    }
  }, [addressBookId]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      await axiosInstance.get(`/AdminSetting/GetAddressBookRecord/${addressBookId}`).then(({ data, status }) => {
        if (status === 200) {
          setFormValues(data);
        }
        setIsLoading(false);
      })
        .catch((error) => {
          setIsLoading(false);
          console.log(error);
        });
    } catch (error: any) {
      setIsLoading(false);
      console.log(error);
    }
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    validate(false, name, value);
  };

  const validate = async (validateAll: boolean, fieldName: string | null = null, value: any | null = null): Promise<FormErrorFields> => {
    return await new Promise((resolve, reject) => {
      let errors: FormErrorFields = { ...formErrors };
      try {
        if (validateAll) {
          errors = {
            email: !formValues?.email ? 'Email is required' : (!utils.isValidEmail(formValues.email) ? 'Please provide a valid email' : ''),
          };
        } else {
          switch (fieldName) {
            case 'email':
              errors.email = !value ? 'Email is required' : (!utils.isValidEmail(value) ? 'Please provide a valid email' : '');
              break;
            default:
              break;
          }
        }
        setFormErrors(errors);
        resolve(errors);
      } catch (error) {
        console.log(`Validation error`);
        reject(errors);
      }
    });
  }

  const handleSubmit = async () => {
    const errors = await validate(true);
    const hasErrors = Object.values(errors).some((error) => error);
    if (!hasErrors) {
      // handle form submission 
      let payload = { ...formValues };
      if (payload.id === undefined) {
        payload.id = 0;
      }
      payload.email = payload.email ? payload.email.trim() : '';
      axiosInstance.post('/AdminSetting/AddUpdateAddressBook', payload).then(({ data, status, statusText }) => {
        if (status === 200) {
          if (addressBookId === 0) {
            toast.success('Email added successfully');
          } else {
            toast.success('Email updated successfully');
          }
          navigate('/address_books');
        }
      });
    }
  };

  const handleDelete = () => {
    const options: SweetAlertOptions = {
      title: 'Are you sure you want to delete?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
    };
    Swal.fire(options).then((result) => {
      if (result.isConfirmed) {
        const payload = {
          id: addressBookId,
          email: formValues.email,
          isDeleted: true
        };
        axiosInstance.post('/AdminSetting/AddUpdateAddressBook', payload).then(({ status }) => {
          if (status === 200) {
            toast.success('Email deleted successfully');
            navigate('/address_books');
          }
        });
      }
    });
  };

  const buttonToggle = isNavOpen ? "toggle-nav toggle-nav--active" : "toggle-nav ";

  return (
    <section className="section" style={{
      minHeight: '100vh'
    }}>
      <div className="section__head">
        <div className="section__head-content">
          <Header as="h1">{addressBookId === 0 ? 'Add' : 'Edit'} Email</Header>
        </div>
        <div className="section__actions">
          <UserInfo />
          <Button
            basic
            onClick={() => {
              handleThemeChange();
            }}
          >
            <Button.Content visible>
              <ReactSVG
                src={BrightnessContrast}
              />
            </Button.Content>
          </Button>
          <Button
            basic
            className={buttonToggle}
            onClick={(event: any) =>
              handleShowNav(event)
            }
          >
            <span></span>
            <span></span>
            <span></span>
          </Button>
        </div>
      </div>
      <div className='section__body'>
        {
          <Dimmer.Dimmable as="div" dimmed={isLoading}>
            <Dimmer active={isLoading} inverted={true}>
              <Loader>Loading</Loader>
            </Dimmer>
            <Grid padded>
              <Grid.Row>
                <Grid.Column mobile={16} tablet={8} computer={8}>
                  <Form className='form-custom'>
                    <Form.Field>
                      <Form.Input
                        label="Email"
                        name="email"
                        value={formValues.email}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        error={!!formErrors?.email}
                        required
                        maxLength={50}
                      />
                      {formValues.email?.length === 50 && <div className="error-message" style={{ color: 'red' }}>Maximum character limit exceeded</div>}
                      {formErrors?.email && <div className="error-message" style={{ color: 'red' }}>{formErrors?.email}</div>}
                    </Form.Field>
                    <Grid>
                      <Grid.Column>
                        <Button type="submit" onClick={handleSubmit} className='violet right floated button' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem' }}>
                          {addressBookId === 0 ? 'Save' : 'Update'}
                        </Button> {' '}
                        <Button type="button" className='second right floated button' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem', color: 'white' }} onClick={() => navigate('/address_books')}>
                          Cancel
                        </Button>
                        {addressBookId > 0 &&
                          <Button type="button" className='danger button'
                            title="Delete"
                            style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem', color: 'white' }}
                            onClick={handleDelete}>
                            <Icon name="trash alternate outline" style={{ marginLeft: '5px' }} />
                          </Button>
                        }
                      </Grid.Column>
                    </Grid>
                  </Form>
                </Grid.Column>
              </Grid.Row>
            </Grid>
            <br />
          </Dimmer.Dimmable>
        }
      </div>
    </section>
  );
}

export default AddressBookForm;